// src/RemoteControlModal.js
// (Quick Manage modal sits next to the legacy "🎮 Control" iframe — see import below.)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import promediaService from '../services/promediaService';
import './RemoteControlModal.css';
import { getRuntimeConfig } from '../config';
import DeviceQuickManageModal from './DeviceQuickManageModal';

// Simple text-only file picker for the "Display now / Media file" row.
// (Earlier versions tried to render WebDAV thumbnails or kind-icon
// placeholders; both were confusing — WebDAV triggered Basic Auth, and the
// placeholder icons gave admins the false impression that thumbnails were
// loading but broken. Text + filename is cleaner. Bring real previews
// back later via a same-origin /api/files proxy if/when needed.)
function MediaFileDropdown({ files, selected, onSelect, disabled }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const placeholder = files.length === 0 ? 'No media files' : 'Select media file…';

  return (
    <div ref={wrapRef} className="rc2-select" style={{ position: 'relative', padding: 0 }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{
          width: '100%', height: '100%', padding: '0 10px',
          background: 'transparent', border: 'none', textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, color: '#374151',
          display: 'flex', alignItems: 'center', gap: 8,
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
        }}
        title={selected || placeholder}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: selected ? '#374151' : '#9CA3AF' }}>
          {selected || placeholder}
        </span>
        <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: 10 }}>▾</span>
      </button>

      {open && files.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6,
          maxHeight: 320, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          zIndex: 50,
        }}>
          {files.map((f) => {
            const isSel = f.basename === selected;
            return (
              <div key={f.basename}
                onClick={() => { onSelect(f.basename); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  background: isSel ? '#EFF6FF' : 'transparent',
                  borderBottom: '1px solid #F3F4F6',
                  color: isSel ? '#1D4ED8' : '#374151',
                  fontWeight: isSel ? 600 : 400,
                }}
                onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = '#F9FAFB'; }}
                onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {f.basename}
                </span>
                {isSel && <span style={{ color: '#1D4ED8', fontSize: 11, fontWeight: 600 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Generic keypad cell used by the redesigned modal. Square colored button with
// an SVG icon, plus a small grey label rendered BELOW the button (outside it).
// `wide` doubles the cell width (spans 2 grid columns).
function Rc2Key({ icon, label, color, onClick, disabled, wide, title }) {
  return (
    <div className={`rc2-cell ${wide ? 'wide-2' : ''}`}>
      <button
        type="button"
        className="rc2-key"
        style={{ background: disabled ? '#cfcfcf' : color, color: '#fff' }}
        onClick={onClick}
        disabled={disabled}
        title={title || label}
        aria-label={label}
      >{icon}</button>
      <div className="rc2-cell-label">{label}</div>
    </div>
  );
}

const RemoteControlModal = ({ device, onClose, user, onDeviceChanged }) => {
  // Admin-only quick actions (Control / AnyDesk). Falls back to "not admin" if
  // App.js didn't pass a user prop (e.g. older callers); hiding both is safer
  // than exposing them by default.
  const isAdmin = !!(user && (user.is_admin === true || user.role === 'admin'));
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [volume, setVolume] = useState(50);
  const [zones, setZones] = useState({});
  const [selectedZone, setSelectedZone] = useState('Whole screen');
  const [mediaControlTarget, setMediaControlTarget] = useState('background');
  const [textMessage, setTextMessage] = useState('');
  const [displayLength, setDisplayLength] = useState(10);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [screenshotData, setScreenshotData] = useState(null);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({});
  const [liveViewEnabled, setLiveViewEnabled] = useState(false);
  const [liveScreenshot, setLiveScreenshot] = useState(null);
  const [deviceResolution, setDeviceResolution] = useState({ width: null, height: null });
  const [controlMode, setControlMode] = useState('detecting');
  const [lanDetails, setLanDetails] = useState(null);
  const [keyboardText, setKeyboardText] = useState('');
  const [showKeyboardInput, setShowKeyboardInput] = useState(false);
  const [showLayoutDrawer, setShowLayoutDrawer] = useState(false);
  const [showQuickManage, setShowQuickManage] = useState(false);
  // Screen-sleep state (independent of layouts/playlists). Persisted per device
  // in localStorage by promediaService so the button shows the right state
  // when the modal is reopened.
  const [screenAsleep, setScreenAsleep] = useState(() =>
    typeof window !== 'undefined' && (device?.device_uid || device?.device_name)
      ? promediaService.isScreenAsleep(device.device_uid || device.device_name)
      : false
  );

  // Big-preview toggle: maximizes the modal + preview for remote-control work.
  // In big mode the modal goes near-full-viewport, the preview pane fills most
  // of it, the keypad becomes a compact floating bar, and the Display section
  // is hidden (use compact mode for those controls).

  // ENHANCED: File-related state
  const [deviceFiles, setDeviceFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  
  // ENHANCED: Layout-related state
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState('');
  const [currentLayoutStatus, setCurrentLayoutStatus] = useState({
    currentLayout: 'Scheduled',
    isScheduled: true
  });
  const [isPaused, setIsPaused] = useState(false);

// Shell terminal state
  const [showShellTerminal, setShowShellTerminal] = useState(false);
  const [shellCommand, setShellCommand] = useState('');
  const [shellHistory, setShellHistory] = useState([]);
  const [isExecutingShell, setIsExecutingShell] = useState(false);
  const shellInputRef = useRef(null);
  const shellOutputRef = useRef(null);
  const apkFileInputRef = useRef(null);
  const keyboardInputRef = useRef(null);
  const [isInstallingApk, setIsInstallingApk] = useState(false);
  const liveViewRef = useRef(null);
  const dragStartRef = useRef(null);
  const lastConnectedDeviceNameRef = useRef(null);
  const connectInFlightRef = useRef(false);
  const lastObjectUrlRef = useRef(null);
  const bridgeScreenshotSupportRef = useRef(new Map());

  const normalizeScreenshotSrc = useCallback((data) => {
    if (!data || typeof data !== 'string') return null;
    const trimmed = data.trim();
    if (!trimmed) return null;
    if (/^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(trimmed)) return trimmed;

    // Infer mime type from common base64 signatures when we only receive raw base64.
    // PNG: iVBORw0KGgo
    // JPEG: /9j/
    // GIF: R0lGOD
    // WEBP: UklGR
    const head = trimmed.slice(0, 12);
    const mime = head.startsWith('iVBORw0KGgo')
      ? 'image/png'
      : head.startsWith('/9j/')
        ? 'image/jpeg'
        : head.startsWith('R0lGOD')
          ? 'image/gif'
          : head.startsWith('UklGR')
            ? 'image/webp'
            : 'image/png';

    return `data:${mime};base64,${trimmed}`;
  }, []);

  // Ref to store a stable timeout ID
  const statusTimeoutRef = useRef(null);
  const volumeTimeoutRef = useRef(null); // NEW: For debouncing volume changes
  const clearStatusTimeout = () => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  };

  const handleMediaControl = async (action) => {
    if (isLoading || operationInProgress) {
      console.log('⚠️ Operation already in progress, skipping media control');
      return;
    }

    try {
      setIsLoading(true);
      setOperationInProgress(true);

      if (mediaControlTarget === 'background') {
        let result;
        if (action === 'prev') {
          setStatus('Background music: Previous via MQTT...');
          result = await promediaService.playPreviousBackground();
        } else if (action === 'next') {
          setStatus('Background music: Next via MQTT...');
          result = await promediaService.playNextBackground();
        } else {
          if (isPaused) {
            setStatus('Background music: Resume via MQTT...');
            result = await promediaService.resumeBackground();
            setIsPaused(false);
            setTimedStatus('✅ Background music resumed', 1500);
          } else {
            setStatus('Background music: Pause via MQTT...');
            result = await promediaService.pauseBackground();
            setIsPaused(true);
            setTimedStatus('✅ Background music paused', 1500);
          }
        }

        if (result && result.success === false) {
          setTimedStatus(`❌ Background music failed: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
        } else if (result && action !== 'play_pause') {
          setTimedStatus('✅ Background music updated', 1500);
        }

        return;
      }

      const mappedAction =
        action === 'prev'
          ? 'Play Previous'
          : action === 'next'
            ? 'Play Next'
            : 'Play/Pause';
      await handlePlaybackControl(mappedAction);
    } catch (error) {
      console.error('❌ Media control error:', error);
      setTimedStatus(`❌ Media control error: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  const openBridgeWebUi = async () => {
    // 2026-05-19 — bridge routes by MQTT identifier (device_uid). Earlier
    // code passed device_name here, which broke once admins started using
    // friendly display labels different from the UID — the bridge would
    // proxy to a topic no agent subscribed to and the request timed out
    // after 30 s. Use device_uid (falling back to device_name only for
    // legacy rows where they're still identical).
    const mqttId = device?.device_uid || device?.device_name;
    if (!mqttId) {
      setTimedStatus('❌ Device UID not available', 3000);
      return;
    }

    try {
      await promediaService.openMqttBridge(mqttId);
    } catch (e) {
      setTimedStatus(`❌ Control open failed: ${e?.message || e}`, 4000);
    }
  };

  const sendKeyEvent = async (keyCode, label) => {
    try {
      setStatus(`Sending key: ${label || keyCode}...`);
      await promediaService.executeShell(`input keyevent ${keyCode}`);
      setTimedStatus(`✅ Key sent: ${label || keyCode}`, 2000);
    } catch (error) {
      console.error('❌ Key event error:', error);
      setTimedStatus(`❌ Key error: ${error.message}`, 3000);
    }
  };

  const sendKeyboardText = async () => {
    const text = keyboardText.trim();
    if (!text) return;

    try {
      // Encode spaces as %s for input text command and escape single quotes
      const encoded = text.replace(/'/g, "\\'").replace(/\s+/g, '%s');
      setStatus(`Sending text: "${text}"...`);
      await promediaService.executeShell(`input text '${encoded}'`);
      setTimedStatus('✅ Text sent', 2000);
      setKeyboardText('');
      
      // Keep keyboard panel open for more input, just clear the text
      if (keyboardInputRef.current) {
        keyboardInputRef.current.focus();
      }
    } catch (error) {
      console.error('❌ Text input error:', error);
      setTimedStatus(`❌ Text error: ${error.message}`, 3000);
    }
  };

  const handleKeyboardKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendKeyboardText();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowKeyboardInput(false);
      setKeyboardText('');
    }
  };

  const getBaseStatus = useCallback(() => {
    if (isConnecting) return 'Connecting to Device...';
    if (isConnected) return 'connected - Ready to control';
    return 'Disconnected';
  }, [isConnecting, isConnected]);

  const setTimedStatus = useCallback((message, duration = 2000) => {
    clearStatusTimeout();
    setStatus(message);
    statusTimeoutRef.current = setTimeout(() => {
      setStatus(getBaseStatus());
    }, duration);
  }, [getBaseStatus]);

  // Initialize MQTT connection when modal opens
  useEffect(() => {
    const deviceName = device?.device_name;
    if (deviceName && lastConnectedDeviceNameRef.current !== deviceName) {
      lastConnectedDeviceNameRef.current = deviceName;
      connectToDevice();
    }

    return () => {
      clearStatusTimeout();
    };
  }, [device?.device_name]);

  useEffect(() => {
    let cancelled = false;

    const detect = async () => {
      const deviceName = device?.device_name;
      if (!deviceName) return;
      setControlMode('remote');
      setLanDetails(null);
      if (cancelled) return;
      setStatus(getBaseStatus());
    };

    detect();
    return () => {
      cancelled = true;
    };
  }, [device?.device_name, getBaseStatus]);

  // Monitor connection status
  useEffect(() => {
    const interval = setInterval(() => {
      const status = promediaService.getConnectionStatus();
      setConnectionStatus(status);
      // Keep internal MQTT status updated for debugging, but do not force UI "connected".
      // UI connection should represent a real device handshake (deviceInfo), not just broker connectivity.
      if (!status.connected || !status.deviceSet) {
        setIsConnected(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto-focus keyboard input when opened
  useEffect(() => {
    if (showKeyboardInput && keyboardInputRef.current) {
      keyboardInputRef.current.focus();
    }
  }, [showKeyboardInput]);

  useEffect(() => {
    // Keep the "ready" status in sync when LAN detection finishes after MQTT connects.
    // Only auto-update when we're not showing a transient action message.
    if (!status) {
      setStatus(getBaseStatus());
      return;
    }
    if (status.includes('Ready to control') || status.includes('Connecting to Device') || status === 'Disconnected') {
      setStatus(getBaseStatus());
    }
  }, [getBaseStatus, status]);

  useEffect(() => {
    if (!liveViewEnabled || !isConnected) return;

    let cancelled = false;

    const tryFetchBridgeScreenshot = async (deviceName) => {
      if (!deviceName) return null;
      const cached = bridgeScreenshotSupportRef.current.get(deviceName);
      if (cached && cached.supported === false && Date.now() - cached.lastCheckedAt < 30000) {
        return null;
      }

      const candidates = [
        '/web/screenshot.png',
        '/web/screenshot.jpg',
        '/web/screenshot.jpeg',
        '/screenshot.png',
        '/screenshot.jpg',
        '/screenshot.jpeg'
      ];

      // Try cached-first on the first iteration so the modal paints instantly
      // (~10 ms cache hit vs ~700 ms MQTT round-trip). Subsequent polls use the
      // regular fresh-fetch path.
      const isFirstFetch = !bridgeScreenshotSupportRef.current.has(deviceName);

      for (const p of candidates) {
        try {
          const cacheHint = (isFirstFetch && /\/screenshot\.jpg$/i.test(p)) ? '&cached=ok' : '';
          const url = `/device/${encodeURIComponent(deviceName)}${p}?t=${Date.now()}${cacheHint}`;
          const resp = await fetch(url, { method: 'GET', credentials: 'include' });
          if (!resp.ok) continue;
          const contentType = resp.headers.get('content-type') || '';
          if (!contentType.toLowerCase().includes('image/')) continue;
          const blob = await resp.blob();
          if (!blob || blob.size < 10000) continue;

          const objUrl = URL.createObjectURL(blob);
          bridgeScreenshotSupportRef.current.set(deviceName, { supported: true, lastCheckedAt: Date.now(), path: p });
          return objUrl;
        } catch (e) {
          // try next candidate
        }
      }

      bridgeScreenshotSupportRef.current.set(deviceName, { supported: false, lastCheckedAt: Date.now() });
      return null;
    };

    const loopScreenshots = async () => {
      while (!cancelled && liveViewEnabled && isConnected) {
        // Pause polling while the tab is hidden — saves bandwidth across the
        // fleet and avoids piling up MQTT requests no one is looking at.
        if (typeof document !== 'undefined' && document.hidden) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          continue;
        }
        try {
          if (controlMode === 'lan') {
            // Dedicated backend endpoint returns a real image/png, avoids spinner HTML entirely.
            // 2026-05-19 Phase C — uid-keyed routing for MQTT bridge.
            const mqttId = device.device_uid || device.device_name;
            const token = promediaService.getAuthToken?.() || localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null;
            try {
              const resp = await fetch(`/api/device/${encodeURIComponent(mqttId)}/lan-screenshot`, {
                method: 'GET',
                headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
                credentials: 'include'
              });

              if (!resp.ok) {
                throw new Error(`LAN screenshot failed (${resp.status})`);
              }

              const blob = await resp.blob();
              if (blob.size < 10000) {
                throw new Error('LAN screenshot too small');
              }

              const url = URL.createObjectURL(blob);
              setLiveScreenshot(url);
            } catch (lanError) {
              // Fallback: keep UI working even if LAN screenshot endpoint is failing
              const result = await promediaService.getScreenshot(device?.device_uid || device?.device_name);
              const base64Data = result?.result?.data;
              const src = normalizeScreenshotSrc(base64Data);
              if (src && src.length > 1000) {
                setLiveScreenshot(src);
              }

              // Optional: don't spam LAN endpoint if it's failing
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } else {
            // 2026-05-19 Phase C — bridge keys on MQTT topic = device_uid.
            const deviceName = device?.device_uid || device?.device_name;

            // Prefer HTTP proxy screenshot from mqtt-http-bridge (works even when MQTT screenshot op is unsupported)
            const bridgeUrl = await tryFetchBridgeScreenshot(deviceName);
            if (bridgeUrl) {
              if (lastObjectUrlRef.current) {
                try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch (e) { /* ignore */ }
              }
              lastObjectUrlRef.current = bridgeUrl;
              setLiveScreenshot(bridgeUrl);
            } else {
              const result = await promediaService.getScreenshot(deviceName);
              const base64Data = result?.result?.data;
              const src = normalizeScreenshotSrc(base64Data);
              if (src && src.length > 1000) {
                setLiveScreenshot(src);
              }
            }
          }
        } catch (error) {
          console.error('Live screenshot error:', error);
          // Shorter backoff on error
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // No artificial sleep on success — the MQTT round-trip itself paces the
        // loop (~600-800 ms per frame on a healthy device). A 1 s sleep here was
        // halving the effective fps (was 0.6 fps; now ~1.3-1.6 fps). A tiny 50 ms
        // yield prevents tight-loop pathological cases when the bridge serves
        // cached frames at <10 ms.
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    };

    loopScreenshots();

    return () => {
      cancelled = true;
      if (lastObjectUrlRef.current) {
        try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch (e) { /* ignore */ }
        lastObjectUrlRef.current = null;
      }
    };
  }, [liveViewEnabled, isConnected, controlMode, device?.device_name, normalizeScreenshotSrc]);

  useEffect(() => {
    if (!liveScreenshot) return;
    if (deviceResolution.width && deviceResolution.height) return;

    try {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight && img.naturalWidth >= 200 && img.naturalHeight >= 200) {
          setDeviceResolution({ width: img.naturalWidth, height: img.naturalHeight });
          console.log('📐 Device resolution inferred from screenshot:', {
            width: img.naturalWidth,
            height: img.naturalHeight
          });
        }
      };
      img.src = liveScreenshot;
    } catch (error) {
      console.warn('⚠️ Failed to infer resolution from screenshot:', error.message);
    }
  }, [liveScreenshot, deviceResolution.width, deviceResolution.height]);

  const connectToDevice = async () => {
    if (connectInFlightRef.current) return;
    if (!device?.device_name) return;

    connectInFlightRef.current = true;
    setIsConnecting(true);
    setStatus('Connecting to Device...');

    try {
      // 2026-05-19 Phase C — agents subscribe to MQTT topics keyed on
      // device_uid (the immutable identifier set at first boot). The
      // friendly device_name admin sees + edits is decorrelated. Use UID
      // for everything that touches MQTT; fall back to device_name for any
      // legacy row whose uid column hasn't been backfilled yet.
      const mqttId = device.device_uid || device.device_name;
      promediaService.setDevice(mqttId);

      // 1) Ensure we can reach the broker
      console.log(`🔌 Testing MQTT broker connection (device: ${mqttId}, label: ${device.device_name})`);
      const brokerTest = await promediaService.testConnection();

      if (!brokerTest.success) {
        setStatus(`MQTT connection failed: ${brokerTest.error}`);
        setIsConnected(false);
        return;
      }

      // 2) Probe the device itself (must respond) to consider it connected
      // Use a shorter timeout so the UI doesn't hang forever on offline devices.
      let originalTimeout;
      try {
        originalTimeout = promediaService.commandTimeout;
        promediaService.commandTimeout = Math.min(originalTimeout || 90000, 15000);
        await promediaService.getDeviceInfo(device.device_uid || device.device_name);

        setIsConnected(true);
        setStatus(getBaseStatus());
        setLiveViewEnabled(true);
      } catch (handshakeError) {
        setIsConnected(false);
        setStatus('Connecting to Device...');
      } finally {
        if (typeof originalTimeout !== 'undefined') {
          promediaService.commandTimeout = originalTimeout;
        }
      }

        // Kick an immediate first frame so the preview doesn't stay black while the loop spins up.
        (async () => {
          try {
            const result = await promediaService.getScreenshot(device?.device_uid || device?.device_name);
            const base64Data = result?.result?.data;
            const src = normalizeScreenshotSrc(base64Data);
            if (src && src.length > 1000) {
              setLiveScreenshot(src);
            }
          } catch (e) {
            // Ignore; live loop will retry.
          }
        })();

      // Do not block the UI on expensive device data fetches (WebDAV listing, etc.)
      // This keeps the modal from showing "Connecting..." for a long time and
      // allows the live preview loop to start immediately.
      setIsConnecting(false);
      loadDeviceData().catch((err) => {
        console.warn('⚠️ Background loadDeviceData failed:', err?.message || err);
      });
    } catch (error) {
      console.error('❌ Connection error:', error);
      setStatus(`Connection error: ${error.message}`);
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
      connectInFlightRef.current = false;
    }
  };

// Shell command execution
  const executeShellCommand = async () => {
    if (!shellCommand.trim() || isExecutingShell) return;
    
    const cmd = shellCommand.trim();
    setShellHistory(prev => [...prev, { type: 'command', text: cmd }]);
    setShellCommand('');
    setIsExecutingShell(true);
    
    try {
      const result = await promediaService.executeShell(cmd);
      if (result && result.result !== undefined) {
        if (result.result.stdout) {
          setShellHistory(prev => [...prev, { type: 'stdout', text: result.result.stdout }]);
        }
        if (result.result.stderr) {
          setShellHistory(prev => [...prev, { type: 'stderr', text: result.result.stderr }]);
        }
        if (result.result.result !== 0) {
          setShellHistory(prev => [...prev, { type: 'info', text: `Exit code: ${result.result.result}` }]);
        }
      }
    } catch (error) {
      setShellHistory(prev => [...prev, { type: 'stderr', text: `Error: ${error.message}` }]);
    } finally {
      setIsExecutingShell(false);
      setTimeout(() => shellInputRef.current?.focus(), 100);
    }
  };

// Install APK function
  const handleApkInstall = async (event) => {
    const file = event.target.files[0];
    if (!file || !file.name.toLowerCase().endsWith('.apk')) {
      setShellHistory(prev => [...prev, { type: 'stderr', text: 'Error: Please select a valid APK file' }]);
      return;
    }

    setIsInstallingApk(true);
    setShellHistory(prev => [...prev, { type: 'info', text: `📦 Uploading ${file.name}...` }]);

    try {
      // Upload to device via WebDAV
      const webdavService = (await import('../services/webdavService')).default;
      await webdavService.ensureAuthenticated();
      
      // Get username for WebDAV path
      const username = webdavService.username || JSON.parse(localStorage.getItem('user'))?.username;
      if (!username) {
        throw new Error('No username found for WebDAV upload');
      }
      
      // Upload to user's device folder
      const uploadPath = `/home/${username}/${device.device_name}/${file.name}`;
      
      setShellHistory(prev => [...prev, { type: 'info', text: `📁 Uploading to: ${uploadPath}` }]);
      
      await webdavService.uploadFile(uploadPath, file, (progress) => {
        console.log(`Upload progress: ${progress}%`);
      });
      
      setShellHistory(prev => [...prev, { type: 'stdout', text: `✅ Upload complete!` }]);
      setShellHistory(prev => [...prev, { type: 'info', text: '⏳ Waiting for device sync (3s)...' }]);
      
      // Wait for device to sync
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Prefer common folders first: /sdcard/slideshow and /sdcard/Download
      const slideshowPath = `/sdcard/slideshow/${file.name}`;
      const downloadPath = `/sdcard/Download/${file.name}`;

      let installPath = null;

      // 1) Check /sdcard/slideshow
      const slideshowCheck = await promediaService.executeShell(`ls -la "${slideshowPath}"`);
      if (!slideshowCheck?.result?.stderr?.includes('No such file')) {
        installPath = slideshowPath;
        setShellHistory(prev => [...prev, { type: 'stdout', text: `📍 Found in /sdcard/slideshow: ${slideshowPath}` }]);
      } else {
        // 2) Check /sdcard/Download
        const downloadCheck = await promediaService.executeShell(`ls -la "${downloadPath}"`);
        if (!downloadCheck?.result?.stderr?.includes('No such file')) {
          installPath = downloadPath;
          setShellHistory(prev => [...prev, { type: 'stdout', text: `📍 Found in /sdcard/Download: ${downloadPath}` }]);
        }
      }

      // 3) Fallback: generic /sdcard scan if not found in preferred folders
      if (!installPath) {
        setShellHistory(prev => [...prev, { type: 'info', text: '📲 APK not in slideshow or Download, scanning /sdcard (may take a while)...' }]);

        const escapedName = file.name.replace(/"/g, '\"');
        const findCmd = `find /sdcard -maxdepth 6 -type f -name "${escapedName}"`;
        const findResult = await promediaService.executeShell(findCmd);

        const stdout = findResult?.result?.stdout || '';
        const candidatePaths = stdout
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (candidatePaths.length === 0) {
          setShellHistory(prev => [...prev, { type: 'stderr', text: '❌ APK not found under /sdcard yet. Please wait a bit longer for sync or adjust device sync settings.' }]);
          return;
        }

        installPath = candidatePaths[0];
        setShellHistory(prev => [...prev, { type: 'stdout', text: `📍 Using APK at: ${installPath}` }]);
      }

      // Install the APK from the discovered path
      const result = await promediaService.executeShell(`pm install -r -g "${installPath}"`);
      
      if (result && result.result) {
        if (result.result.stdout) {
          setShellHistory(prev => [...prev, { type: 'stdout', text: result.result.stdout }]);
        }
        if (result.result.stderr) {
          setShellHistory(prev => [...prev, { type: 'stderr', text: result.result.stderr }]);
        }
        if (result.result.result === 0 || (result.result.stdout && result.result.stdout.includes('Success'))) {
          setShellHistory(prev => [...prev, { type: 'info', text: '✅ APK installed successfully!' }]);
        } else {
          setShellHistory(prev => [...prev, { type: 'stderr', text: `❌ Install failed (exit code: ${result.result.result})` }]);
          setShellHistory(prev => [...prev, { type: 'info', text: `💡 Manual: pm install -r -g "${installPath}"` }]);
        }
      }
    } catch (error) {
      setShellHistory(prev => [...prev, { type: 'stderr', text: `❌ Error: ${error.message}` }]);
    } finally {
      setIsInstallingApk(false);
      event.target.value = ''; // Reset file input
    }
  };
  
  // Auto-scroll terminal to bottom
  useEffect(() => {
    if (shellOutputRef.current) {
      shellOutputRef.current.scrollTop = shellOutputRef.current.scrollHeight;
    }
  }, [shellHistory]);

  const loadDeviceData = async () => {
    try {
      console.log('📊 Loading device data via MQTT...');
      
// Get current volume
try {
  const volumeInfo = await promediaService.getVolume();
  console.log('🔊 Volume info received:', volumeInfo);
  if (volumeInfo && volumeInfo.result && typeof volumeInfo.result.currentVolume === 'number') {
    const deviceVolume = volumeInfo.result.currentVolume;

    // Device API returns percentage (0-100) for currentVolume.
    const percentage = Math.round(deviceVolume);
    const clamped = Math.max(0, Math.min(100, percentage));
    setVolume(clamped);
  }
} catch (volumeError) {
  console.warn('⚠️ Could not get volume:', volumeError.message);
}

      // Get zones from device via MQTT
      try {
        console.log('🎯 Loading zones from device via MQTT...');
        const zonesInfo = await promediaService.getZones();
        console.log('🎯 Zones response received:', zonesInfo);
        
        if (zonesInfo && zonesInfo.success !== false && zonesInfo.result) {
          if (typeof zonesInfo.result === 'object' && Object.keys(zonesInfo.result).length > 0) {
            console.log('✅ Using real zones from device:', zonesInfo.result);
            setZones(zonesInfo.result);
            
            const zoneKeys = Object.keys(zonesInfo.result);
            if (zoneKeys.length > 0) {
              setSelectedZone(zoneKeys[0]);
              console.log(`🎯 Default zone set to: ${zoneKeys[0]}`);
            }
          } else if (Array.isArray(zonesInfo.result) && zonesInfo.result.length > 0) {
            const zoneObj = {};
            zonesInfo.result.forEach((zone, index) => {
              if (typeof zone === 'string') {
                zoneObj[zone] = zone;
              } else if (zone.id && zone.name) {
                zoneObj[zone.id] = zone.name;
              } else {
                zoneObj[`zone_${index + 1}`] = zone.toString();
              }
            });
            console.log('✅ Converted array zones to object:', zoneObj);
            setZones(zoneObj);
            setSelectedZone(Object.keys(zoneObj)[0]);
          } else {
            throw new Error('Invalid zones format received from device');
          }
        } else {
          throw new Error('No zones data received from device');
        }
      } catch (zonesError) {
        console.warn('⚠️ Could not get real zones from device:', zonesError.message);
        console.log('🔄 Falling back to default zones...');
        
        const defaultZones = {
          'main': 'Main Zone (Whole Screen)',
          'zone1': 'Zone 1',
          'zone2': 'Zone 2',
          'overlay': 'Overlay Zone'
        };
        
        setZones(defaultZones);
        setSelectedZone('main');
        console.log('📋 Using default zones:', defaultZones);
      }
      
     // 2026-05-20 — list files on the AGENT's local storage (not WebDAV).
     // Previous implementation tried 3 WebDAV listing fallbacks which
     // pulled the sync-source folder; that listed files the device may
     // never have downloaded if sync isn't running. The agent's elFinder
     // `open` API returns its actual on-disk media — exactly what the
     // admin sees in the agent's local file manager. WebDAV fallback
     // kept for legacy devices whose agent doesn't expose the API.
      setIsLoadingFiles(true);
      try {
        console.log('📂 Loading files from device local storage...');
        let files = [];
        const mqttId = device.device_uid || device.device_name;

        try {
          files = await promediaService.listDeviceLocalFiles(mqttId);
          console.log(`✅ Agent local files: ${files.length}`);
        } catch (agentErr) {
          console.warn('⚠️ Agent elFinder API failed, falling back to WebDAV:', agentErr.message);
          // Fallback only if the agent doesn't respond — gives us SOMETHING
          // rather than an empty list for legacy agents.
          if (device.media_files_link) {
            try {
              files = await promediaService.getDeviceFiles(device.media_files_link);
              console.log(`✅ WebDAV fallback: ${files.length} files`);
            } catch (webdavErr) {
              console.warn('⚠️ WebDAV fallback also failed:', webdavErr.message);
              files = [];
            }
          }
        }
        
        // Filter out any duplicates and invalid files
        const uniqueFiles = files.filter((file, index, self) => 
          file && file.basename && 
          self.findIndex(f => f.basename === file.basename) === index
        );
        
        setDeviceFiles(uniqueFiles);
        console.log(`📊 Final result: ${uniqueFiles.length} unique files loaded`);
        
        if (uniqueFiles.length > 0) {
          setSelectedFile(uniqueFiles[0].basename);
          console.log(`🎯 Default file selected: ${uniqueFiles[0].basename}`);
        } else {
          console.log('📭 No files available for selection');
        }
        
      } catch (filesError) {
        console.error('❌ All file loading methods failed:', filesError.message);
        setDeviceFiles([]);
        
        // Show a more informative message to the user
        setTimedStatus(`⚠️ Could not load device files: ${filesError.message}`, 5000);
      } finally {
        setIsLoadingFiles(false);
      }

      // ENHANCED: Load layouts — MQTT direct op first, fall back to HTML scrape
      // through the bridge. The 'layouts' MQTT op isn't implemented on the
      // PromediaSign agent (returns empty), so on real devices we always need
      // the HTML-list fallback (verified live: 2-3 layouts always show up).
      try {
        let names = [];
        try {
          const layoutsInfo = await promediaService.getLayouts(device?.device_uid || device?.device_name);
          if (layoutsInfo?.result?.length > 0) {
            names = layoutsInfo.result.map(l => (typeof l === 'string' ? l : l.name)).filter(Boolean);
          }
        } catch (_) { /* fall through to HTML scrape */ }
        if (names.length === 0) {
          try {
            const list = await promediaService.listLayouts(device?.device_uid || device?.device_name);
            names = (list || []).map(l => l.name).filter(Boolean);
            if (names.length > 0) console.log('✅ Layouts loaded via HTML scrape:', names);
          } catch (e) {
            console.warn('⚠️ Layouts HTML scrape failed:', e.message);
          }
        }
        setLayouts(names);
        if (names.length === 0) console.warn('⚠️ No layouts found on device');
      } catch (layoutsError) {
        console.warn('⚠️ Could not get layout options:', layoutsError.message);
        setLayouts([]);
      }

      // ENHANCED: Load media files via shell. Verified against smali
      // (InitializerImpl.setFilePaths / resolveContentPath in Promedia 4.9.5):
      //   MAIN_PATH    = getExternalFilesDir("slideshow")    // app-scoped
      //   CONTENT_PATH = MAIN_PATH + "slideshow/"            // default
      //                | EXTERNAL_STORAGE_PATH + "/slideshow/" if user setting set
      // Real path on devices: /sdcard/Android/data/sign.promedia.ltd/files/slideshow/
      // Old (Promedia <4.x) installs used /sdcard/slideshow/ — kept as fallback.
      // The agent ALSO writes its rolling logs (log.txt, log-1.txt … log-10.txt)
      // into MAIN_PATH, so we must filter those out.
      try {
        // `find -type f` so we walk subdirs (ads/, side/, etc.). maxdepth 3 keeps
        // it fast on devices with thousands of files.
        const shellRes = await promediaService.executeShell(
          'find /sdcard/Android/data/sign.promedia.ltd/files/slideshow/ ' +
          '/sdcard/slideshow/ ' +
          '-maxdepth 3 -type f 2>/dev/null | head -300'
        );
        const raw = shellRes?.result?.stdout || shellRes?.stdout || '';
        const mediaExtRe = /\.(png|jpg|jpeg|webp|gif|bmp|mp4|mkv|webm|mov|avi|m4v|3gp|ogv)$/i;
        const isAgentLog = (basename) => /^log(-\d+)?\.txt$/i.test(basename) || /\.log$/i.test(basename);

        const entries = raw.split('\n')
          .map(s => s.trim())
          .filter(s => s && !s.startsWith('Errors:'))
          .map(fullPath => {
            const basename = fullPath.split('/').pop();
            return { fullPath, basename };
          })
          .filter(({ basename }) => basename && !basename.startsWith('.') && !isAgentLog(basename) && mediaExtRe.test(basename));

        if (entries.length > 0) {
          setDeviceFiles(prev => {
            const merged = [...prev];
            const seen = new Set(prev.map(f => f.basename));
            entries.forEach(({ basename }) => {
              if (!seen.has(basename)) merged.push({ basename, size: 0, modified: '' });
            });
            return merged;
          });
          console.log(`✅ Found ${entries.length} media file(s) on device (across subdirs)`);
        } else {
          console.log('📭 No media files found — device may have custom EXTERNAL_STORAGE_PATH set, or no media synced yet');
        }
      } catch (filesShellErr) {
        console.warn('⚠️ Shell ls /sdcard/slideshow failed:', filesShellErr.message);
      }

      // ENHANCED: Get current layout status
      try {
        console.log('🖥️ Getting current layout status...');
        const layoutStatus = await promediaService.getCurrentLayout(device?.device_name);
        console.log('🖥️ Current layout status:', layoutStatus);
        
        if (layoutStatus && layoutStatus.success !== false && layoutStatus.result) {
          setCurrentLayoutStatus(layoutStatus.result);
        }
      } catch (layoutStatusError) {
        console.warn('⚠️ Could not get current layout status:', layoutStatusError.message);
      }

      // Get device info for debugging
      try {
        const deviceInfo = await promediaService.getDeviceInfo(device?.device_name);
        console.log('🔧 Device Info received:', deviceInfo);
        if (deviceInfo && deviceInfo.result) {
          const width = deviceInfo.result.screenWidth || deviceInfo.result.width || null;
          const height = deviceInfo.result.screenHeight || deviceInfo.result.height || null;
          if (width && height) {
            setDeviceResolution({ width, height });
            console.log('📐 Device resolution set from deviceInfo:', { width, height });
          }
        }
      } catch (infoError) {
        console.warn('⚠️ Could not get device info:', infoError.message);
      }

    } catch (error) {
      console.error('❌ Error loading device data:', error);
      setStatus(`Error loading device data: ${error.message}`);
    }
  };

// Handle volume slider changes with debouncing
const handleVolumeChange = (newVolume) => {
  // Update UI immediately for smooth slider movement
  setVolume(newVolume);
  
  // Clear any pending volume command
  if (volumeTimeoutRef.current) {
    clearTimeout(volumeTimeoutRef.current);
  }
  
  // Wait 500ms after user stops dragging before sending command
  volumeTimeoutRef.current = setTimeout(async () => {
    if (isLoading || operationInProgress) {
      console.log('⚠️ Operation in progress, will retry volume change');
      // Retry after a short delay
      setTimeout(() => handleVolumeChange(newVolume), 500);
      return;
    }
    
    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus(`Setting volume to ${newVolume}%...`);
      
      // Keep old behavior: send slider value directly.
      const result = await promediaService.setVolume(newVolume);
      
      if (result && result.success !== false) {
        setTimedStatus(`✅ Volume set to ${newVolume}%`, 2000);
      } else {
        setTimedStatus(`❌ Volume error: ${result?.error || result?.errorMessage || 'Unknown'}`, 3000);
      }
    } catch (error) {
      console.error('❌ Volume error:', error);
      setTimedStatus(`❌ Volume error: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  }, 500); // Wait 500ms after user stops moving slider
};

  const handleLiveViewTap = async (event) => {
    const imgSource = liveScreenshot || screenshotData;
    if (!imgSource) {
      return;
    }

    if (!deviceResolution.width || !deviceResolution.height) {
      setTimedStatus('⚠️ Screen resolution unknown, cannot send touch', 3000);
      return;
    }

    if (!liveViewRef.current) {
      return;
    }

    try {
      const rect = liveViewRef.current.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;

      const ratioX = rect.width > 0 ? clickX / rect.width : 0;
      const ratioY = rect.height > 0 ? clickY / rect.height : 0;

      const touchX = Math.round(ratioX * deviceResolution.width);
      const touchY = Math.round(ratioY * deviceResolution.height);

      console.log('📍 Live view tap -> touch coordinates:', {
        clickX,
        clickY,
        ratioX,
        ratioY,
        touchX,
        touchY
      });

      setStatus(`Sending touch at ${touchX} x ${touchY} via MQTT shell...`);
      await promediaService.executeShell(`input tap ${touchX} ${touchY}`);
      setTimedStatus(`✅ Touch sent at ${touchX} x ${touchY}`, 2000);
    } catch (error) {
      console.error('❌ Live view tap error:', error);
      setTimedStatus(`❌ Touch error: ${error.message}`, 3000);
    }
  };

  const handleLiveViewMouseDown = (event) => {
    if (!liveViewRef.current) return;
    const rect = liveViewRef.current.getBoundingClientRect();
    dragStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      rect
    };
  };

  const handleLiveViewMouseUp = async (event) => {
    const start = dragStartRef.current;
    dragStartRef.current = null;

    if (!start || !liveViewRef.current) {
      return;
    }

    const dx = event.clientX - start.clientX;
    const dy = event.clientY - start.clientY;
    const distanceSquared = dx * dx + dy * dy;

    // If movement is small, treat as a tap
    if (distanceSquared < 25) {
      await handleLiveViewTap(event);
      return;
    }

    if (!deviceResolution.width || !deviceResolution.height) {
      setTimedStatus('⚠️ Screen resolution unknown, cannot send drag', 3000);
      return;
    }

    try {
      const rect = liveViewRef.current.getBoundingClientRect();

      const startX = start.clientX - rect.left;
      const startY = start.clientY - rect.top;
      const endX = event.clientX - rect.left;
      const endY = event.clientY - rect.top;

      const ratioStartX = rect.width > 0 ? startX / rect.width : 0;
      const ratioStartY = rect.height > 0 ? startY / rect.height : 0;
      const ratioEndX = rect.width > 0 ? endX / rect.width : 0;
      const ratioEndY = rect.height > 0 ? endY / rect.height : 0;

      const touchStartX = Math.round(ratioStartX * deviceResolution.width);
      const touchStartY = Math.round(ratioStartY * deviceResolution.height);
      const touchEndX = Math.round(ratioEndX * deviceResolution.width);
      const touchEndY = Math.round(ratioEndY * deviceResolution.height);

      console.log('📍 Live view drag -> swipe coordinates:', {
        touchStartX,
        touchStartY,
        touchEndX,
        touchEndY
      });

      setStatus(`Sending drag from ${touchStartX},${touchStartY} to ${touchEndX},${touchEndY}...`);
      await promediaService.executeShell(`input swipe ${touchStartX} ${touchStartY} ${touchEndX} ${touchEndY} 300`);
      setTimedStatus('✅ Drag sent', 2000);
    } catch (error) {
      console.error('❌ Live view drag error:', error);
      setTimedStatus(`❌ Drag error: ${error.message}`, 3000);
    }
  };

  const handlePlaybackControl = async (action) => {
    if (isLoading || operationInProgress) {
      console.log('⚠️ Operation already in progress, skipping playback control');
      return;
    }
    
    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus(`${action} via MQTT...`);
      
      const zoneParam = selectedZone;
      console.log(`🎮 Executing ${action} via MQTT for zone: ${zoneParam}`);
      
      let result;
      switch (action) {
        case 'Play Previous':
          result = await promediaService.playPrevious(zoneParam);
          break;
        case 'Play Next':
          result = await promediaService.playNext(zoneParam);
          break;
        case 'Play/Pause':
          if (isPaused) {
            result = await promediaService.resumePlayback(zoneParam);
            setIsPaused(false);
            setTimedStatus(`✅ Resumed playback`);
          } else {
            result = await promediaService.pausePlayback(zoneParam);
            setIsPaused(true);
            setTimedStatus(`✅ Paused playback`);
          }
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      console.log(`🎮 ${action} result:`, result);
      
      if (result && result.success !== false && action !== 'Play/Pause') {
        setTimedStatus(`✅ ${action} completed successfully`);
      } else if (result && result.success === false) {
        setTimedStatus(`❌ ${action} failed: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
      }
    } catch (error) {
      console.error(`❌ ${action} error:`, error);
      setTimedStatus(`❌ ${action} error: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  const handleShowMessage = async () => {
    if (!textMessage.trim() || isLoading || operationInProgress) {
      setTimedStatus('Please enter a message', 1500);
      return;
    }
    
    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus('Showing message via MQTT...');
      
      const zoneParam = selectedZone;
      console.log(`💬 Showing HTML message via MQTT: "${textMessage}" for ${displayLength}s in zone: ${zoneParam}`);
      
      const html = `<div style="display: flex; align-items: center; justify-content: center; height: 100vh; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); font-family: Arial, sans-serif;">
        <h1 style="color: white; font-size: 4em; text-align: center; text-shadow: 2px 2px 4px rgba(0,0,0,0.5); padding: 40px; border-radius: 20px; background: rgba(255,255,255,0.1); backdrop-filter: blur(10px);">
          ${textMessage}
        </h1>
      </div>`;
      
      const result = await promediaService.showHtml(html, displayLength, zoneParam);
      
      console.log(`💬 Show message result:`, result);
      
      if (result && result.success !== false) {
        setTimedStatus('✅ Message displayed successfully!', 3000);
      } else {
        setTimedStatus(`❌ Show message failed: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
      }
    } catch (error) {
      console.error('❌ Show message error:', error);
      setTimedStatus(`❌ Show message error: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };
  
  const handleShowFile = async () => {
    if (!selectedFile || isLoading || operationInProgress) {
      setTimedStatus('Please select a file', 1500);
      return;
    }
    
    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus('Showing file via MQTT...');
      
      const zoneParam = selectedZone;
      console.log(`🖼️ Showing file "${selectedFile}" via MQTT for ${displayLength}s in zone: ${zoneParam}`);
      
      const result = await promediaService.showFile(selectedFile, displayLength, zoneParam);
      
      console.log(`🖼️ Show file result:`, result);
      
      if (result && result.success !== false) {
        setTimedStatus(`✅ File "${selectedFile}" displayed successfully!`, 3000);
      } else {
        setTimedStatus(`❌ Show file failed: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
      }
    } catch (error) {
      console.error('❌ Show file error:', error);
      setTimedStatus(`❌ Show file error: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  // ENHANCED: Layout control with dropdown
  const handleSetLayout = async () => {
    if (!selectedLayout || isLoading || operationInProgress) {
      setTimedStatus('Please select a layout', 1500);
      return;
    }
    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus(`Setting layout "${selectedLayout}" via MQTT...`);
      
      console.log(`🖥️ Setting layout "${selectedLayout}" via MQTT`);
      
      const result = await promediaService.setLayout(selectedLayout);
      
      console.log(`🖥️ Set layout result:`, result);
      
      if (result && result.success !== false) {
        setTimedStatus(`✅ Layout "${selectedLayout}" set successfully!`, 3000);
        setCurrentLayoutStatus({
          currentLayout: selectedLayout,
          isScheduled: false
        });
      } else {
        setTimedStatus(`❌ Failed to set layout: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
      }
    } catch (error) {
      console.error('❌ Set layout error:', error);
      setTimedStatus(`❌ Error setting layout: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  const handleClearLayout = async () => {
    if (isLoading || operationInProgress) return;
    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus('Clearing layout via MQTT...');
      
      console.log(`🖥️ Clearing layout via MQTT`);
      
      const result = await promediaService.clearLayout();
      
      console.log(`🖥️ Clear layout result:`, result);
      
      if (result && result.success !== false) {
        setTimedStatus('✅ Layout cleared successfully! Back to scheduled layout.', 3000);
        setCurrentLayoutStatus({
          currentLayout: 'Scheduled',
          isScheduled: true
        });
        setSelectedLayout('');
      } else {
        setTimedStatus(`❌ Failed to clear layout: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
      }
    } catch (error) {
      console.error('❌ Clear layout error:', error);
      setTimedStatus(`❌ Error clearing layout: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  const handleDeviceAction = async (action) => {
    // Special lightweight path for beep so it's instant and doesn't block other actions
    if (action === 'Play Beep') {
      try {
        setStatus('Play Beep via MQTT...');
        console.log('🔧 Executing device action "Play Beep" via MQTT');
        const result = await promediaService.playBeep();
        console.log('🔧 Device action "Play Beep" result:', result);
        if (result && result.success !== false) {
          setTimedStatus('✅ Beep played successfully', 1500);
        } else if (result && result.success === false) {
          setTimedStatus(`❌ Beep failed: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
        }
      } catch (error) {
        console.error('❌ Device action "Play Beep" error:', error);
        setTimedStatus(`❌ Beep error: ${error.message}`, 3000);
      }
      return;
    }

    // Sleep toggle: black out the screen WITHOUT powering the device off. Works
    // without any pre-created "sleep" layout/playlist — uses the agent's
    // showsenthtml direct op to render full-black HTML on every main panel,
    // and playlist/clear to wake (returns each panel to its scheduled content).
    // Device stays online, MQTT/sync/brightness keep working — only the picture
    // is black.
    if (action === 'Toggle Sleep') {
      if (isLoading || operationInProgress) {
        console.log('⚠️ Operation already in progress, skipping sleep toggle');
        return;
      }

      try {
        setIsLoading(true);
        setOperationInProgress(true);

        const deviceName = device?.device_name;
        const wasAsleep = promediaService.isScreenAsleep(deviceName);

        if (wasAsleep) {
          setStatus('Waking screen…');
          console.log('☀ Waking screen — clearing playlists on all main panels');
          const result = await promediaService.screenWake(deviceName);
          console.log('☀ Wake result:', result);
          setScreenAsleep(false);
          if (result?.success !== false) {
            setTimedStatus('☀ Screen woken (back to scheduled content)', 3000);
          } else {
            setTimedStatus('⚠ Wake sent but some panels did not respond', 4000);
          }
        } else {
          setStatus('Putting screen to sleep…');
          console.log('💤 Sleeping screen — sending black HTML to all main panels');
          const result = await promediaService.screenSleep(deviceName);
          console.log('💤 Sleep result:', result);
          setScreenAsleep(true);
          if (result?.success !== false) {
            setTimedStatus(`💤 Screen asleep (${result.panels.length} panel${result.panels.length === 1 ? '' : 's'} blacked out) — device still online`, 3000);
          } else {
            setTimedStatus('⚠ Sleep sent but some panels did not respond', 4000);
          }
        }
      } catch (error) {
        console.error('❌ Device action "Toggle Sleep" error:', error);
        setTimedStatus(`❌ Sleep toggle error: ${error.message}`, 4000);
      } finally {
        setIsLoading(false);
        setOperationInProgress(false);
      }

      return;
    }

    if (isLoading || operationInProgress) return;
    
    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus(`${action} via MQTT...`);
      
      console.log(`🔧 Executing device action "${action}" via MQTT`);
      
      let result;
      switch (action) {
        case 'Reload App':
          result = await promediaService.reloadApp();
          break;
        case 'Reboot Device':
          if (window.confirm('Are you sure you want to reboot the device?')) {
            result = await promediaService.rebootDevice();
          } else {
            setStatus('Reboot cancelled');
            return;
          }
          break;
        case 'Screenshot':
          setLiveViewEnabled(true);
          result = await promediaService.getScreenshot(device?.device_uid || device?.device_name);
          if (result && result.success !== false && result.result && result.result.data) {
            const base64Data = result.result.data;
            const src = normalizeScreenshotSrc(base64Data);
            if (src && src.length > 1000) {
              setScreenshotData(src);
              setShowScreenshot(true);
              setTimedStatus('✅ Screenshot captured successfully! Live control enabled.');
            } else {
              setTimedStatus('❌ Screenshot data invalid (too small)', 3000);
            }
          } else {
            setTimedStatus('❌ No screenshot data received', 3000);
          }
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      console.log(`🔧 Device action "${action}" result:`, result);
      
      if (result && result.success !== false && action !== 'Screenshot') {
        setTimedStatus(`✅ ${action} completed successfully`, 3000);
      } else if (result && result.success === false) {
        setTimedStatus(`❌ ${action} failed: ${result?.error || result?.errorMessage || 'Unknown error'}`, 3000);
      }
    } catch (error) {
      console.error(`❌ Device action "${action}" error:`, error);
      setTimedStatus(`❌ ${action} error: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  const handlePowerButtonClick = async () => {
    if (isLoading || operationInProgress) return;

    try {
      setIsLoading(true);
      setOperationInProgress(true);
      setStatus('Shutting down device via MQTT shell...');

      const result = await promediaService.executeShell('reboot -p');
      console.log('🔧 Shutdown command result:', result);
      setTimedStatus('✅ Shutdown command sent', 3000);
    } catch (error) {
      console.error('❌ Shutdown error:', error);
      setTimedStatus(`❌ Shutdown error: ${error.message}`, 3000);
    } finally {
      setIsLoading(false);
      setOperationInProgress(false);
    }
  };

  // Inline AnyDesk popup — preserves the existing manual-DOM modal so we
  // don't change any of its copy-password / launch logic during the redesign.
  const openAnyDeskPopup = (e) => {
    e?.stopPropagation?.();
    if (!device.is_online || !device.anydesk_id) return;
    const overlay = document.createElement('div');
    overlay.className = 'anydesk-modal-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:1000000;display:flex;justify-content:center;align-items:center';
    const box = document.createElement('div');
    box.style.cssText = 'background:white;padding:20px;border-radius:8px;max-width:500px;width:90%;box-shadow:0 2px 10px rgba(0,0,0,0.2)';
    const hiddenPass = '*'.repeat(device.anydesk_password ? device.anydesk_password.length : 0);
    box.innerHTML = `
      <h3 style="margin-top:0">Connect to ${device.device_name}</h3>
      <p>AnyDesk ID: <strong>${device.anydesk_id}</strong></p>
      <p>Password: <strong id="ad-pwd">${hiddenPass}</strong>
        <button id="ad-copy" style="margin-left:10px;padding:3px 8px;background:#673ab7;color:white;border:none;border-radius:4px;cursor:pointer;">Copy Password</button>
        <span id="ad-status" style="margin-left:8px;font-size:12px;"></span>
      </p>
      <div style="display:flex;justify-content:space-between;margin-top:15px;">
        <button id="ad-launch" style="padding:8px 12px;background:#2196f3;color:white;border:none;border-radius:4px;cursor:pointer;">Launch AnyDesk</button>
        <button id="ad-close" style="padding:8px 12px;background:#f44336;color:white;border:none;border-radius:4px;cursor:pointer;">Close</button>
      </div>
      <textarea id="ad-ta" style="position:absolute;left:-9999px;top:0;opacity:0;">${device.anydesk_password || ''}</textarea>`;
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    const close = () => { try { document.body.removeChild(overlay); } catch (_) {} };
    setTimeout(() => {
      const copyBtn = document.getElementById('ad-copy');
      const ta = document.getElementById('ad-ta');
      const st = document.getElementById('ad-status');
      copyBtn?.addEventListener('click', () => {
        try {
          ta.style.display = 'block'; ta.select(); ta.setSelectionRange(0, 99999);
          document.execCommand('copy'); ta.style.display = 'none';
          if (st) { st.textContent = '✅ Copied!'; st.style.color = '#4caf50';
            setTimeout(() => { if (st) st.textContent = ''; }, 2000);
          }
        } catch (err) {
          if (st) { st.textContent = '❌ Failed to copy'; st.style.color = '#f44336'; }
        }
      });
      document.getElementById('ad-launch')?.addEventListener('click', () => {
        window.open(`anydesk:${device.anydesk_id}`, '_blank');
        close();
      });
      document.getElementById('ad-close')?.addEventListener('click', close);
      overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
    }, 50);
  };

  // Reusable SVG icons for the keypad — small, monochrome, line-style.
  const I = {
    speakerX:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>,
    bell:        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    arrLeft:     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
    arrRight:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
    arrUp:       <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>,
    arrDown:     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
    home:        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    menu:        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
    back:        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
    ok:          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    clipboard:   <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1z"/><rect x="4" y="5" width="16" height="17" rx="2"/></svg>,
    keyboard:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="10" x2="6" y2="10"/><line x1="10" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="14" y2="10"/><line x1="18" y1="10" x2="18" y2="10"/><line x1="7" y1="14" x2="17" y2="14"/></svg>,
    rotate:      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
    gamepad:     <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="11" x2="10" y2="11"/><line x1="8" y1="9" x2="8" y2="13"/><line x1="15" y1="12" x2="15.01" y2="12"/><line x1="18" y1="10" x2="18.01" y2="10"/><path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z"/></svg>,
    cast:        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 16.1A5 5 0 0 1 5.9 20"/><path d="M2 12.05A9 9 0 0 1 9.95 20"/><path d="M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6"/><line x1="2" y1="20" x2="2.01" y2="20"/></svg>,
    moonZ:       <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
    sun:         <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/></svg>,
    power:       <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64A9 9 0 0 1 20.77 15"/><path d="M6.64 6.64A9 9 0 1 0 12 21a9 9 0 0 0 5.36-1.78"/><line x1="12" y1="2" x2="12" y2="12"/></svg>,
    play:        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
    pause:       <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
    skipBack:    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><polygon points="19 20 9 12 19 4 19 20"/><rect x="5" y="5" width="2" height="14"/></svg>,
    skipFwd:     <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 4 15 12 5 20 5 4"/><rect x="17" y="5" width="2" height="14"/></svg>,
    msg:         <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    layout:      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>,
    film:        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>,
    check:       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    schedule:    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  };

  return (
    <div className="remote-control-modal-overlay" onClick={onClose}>
      <div className="remote-control-modal-content" onClick={e => e.stopPropagation()}>
        <div className="remote-control-modal-header rc2-header">
          <h2 className="rc2-title">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 8 }}><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            <span>Device — <strong>{device?.device_name}</strong></span>
          </h2>
          <button className="remote-control-close-button" onClick={onClose}>×</button>
        </div>

        <div className="remote-control-modal-body rc2-body">
          {/* Connection Status with Quick Keys */}
          <div className="remote-control-status-section">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px'
              }}
            >
              <div style={{ flex: '2 1 0', minWidth: 0 }}>
                <div className={`remote-control-connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                  {isConnected ? '✅ Connected' : isConnecting ? '🔄 Connecting...' : '❌ Disconnected'}
                </div>
                <div className="remote-control-status-message">
                  <span>{status}</span>
                </div>
              </div>

              {isConnected && (
                <div className="rc2-top-actions" style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <div className="rc2-top-cell">
                  <button
                    onClick={() => handleDeviceAction('Toggle Sleep')}
                    disabled={isLoading}
                    title={screenAsleep ? 'Screen is asleep — click to wake' : 'Black out the screen (device stays online)'}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '999px',
                      border: screenAsleep ? '2px solid #FFC107' : 'none',
                      background: screenAsleep ? '#FFC107' : '#000',
                      color: screenAsleep ? '#000' : '#fff',
                      fontSize: '18px',
                      cursor: 'pointer',
                      transition: 'background 0.15s, border-color 0.15s, color 0.15s'
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="26"
                      height="26"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                      style={{ display: 'block', margin: '0 auto' }}
                    >
                      <circle cx="12" cy="12" r="10" fill="#000" />
                      <path
                        d="M14.8 3.4a8.4 8.4 0 1 0 4.2 15.7 7.4 7.4 0 1 1-4.2-15.7z"
                        fill="#fff"
                      />
                      <text x="14.0" y="9.8" fill="#fff" fontSize="5.4" fontFamily="Arial" fontWeight="700">Z</text>
                      <text x="17.2" y="7.8" fill="#fff" fontSize="4.4" fontFamily="Arial" fontWeight="700">z</text>
                      <text x="19.1" y="6.3" fill="#fff" fontSize="3.6" fontFamily="Arial" fontWeight="700">z</text>
                    </svg>
                  </button>
                  <div className="rc2-top-label">{screenAsleep ? 'Wake' : 'Sleep'}</div>
                  </div>
                  <div className="rc2-top-cell">
                  <button
                    onClick={() => handleDeviceAction('Reboot Device')}
                    disabled={isLoading}
                    title="Reboot"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '999px',
                      border: 'none',
                      background: '#2E7D32',
                      color: '#fff',
                      fontSize: '18px',
                      cursor: 'pointer'
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      focusable="false"
                      style={{ display: 'block', margin: '0 auto' }}
                    >
                      <path d="M21 12a9 9 0 0 1-15.42 6.36" />
                      <path d="M3 12a9 9 0 0 1 15.42-6.36" />
                      <polyline points="3 16 3 12 7 12" />
                      <polyline points="21 8 21 12 17 12" />
                    </svg>
                  </button>
                  <div className="rc2-top-label">Reboot</div>
                  </div>
                  <div className="rc2-top-cell">
                  <button
                    onClick={handlePowerButtonClick}
                    disabled={isLoading}
                    title="Power"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '999px',
                      border: 'none',
                      background: '#D32F2F',
                      color: '#fff',
                      fontSize: '18px',
                      cursor: 'pointer'
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      focusable="false"
                      style={{ display: 'block', margin: '0 auto' }}
                    >
                      <path d="M12 2v10" />
                      <path d="M6.38 6.38a9 9 0 1 0 11.24 0" />
                    </svg>
                  </button>
                  <div className="rc2-top-label">Power</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {isConnected && (
            <>
              {/* Live View — click/touch sends `input tap X Y` to the device.
                  Tap reaction will be ~1.5 s on the original APK (frame fetch
                  ~700 ms + shell ~700 ms). It works; just don't expect
                  scrcpy-grade responsiveness until we ship the modded APK. */}
              <div className="remote-control-section">
                <div
                  ref={liveViewRef}
                  className="remote-control-live-view"
                  onMouseDown={handleLiveViewMouseDown}
                  onMouseUp={handleLiveViewMouseUp}
                  style={{
                    marginTop: '8px',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    background: '#000',
                    cursor: 'crosshair',
                    minHeight: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {(liveScreenshot || screenshotData) && (
                    <img
                      src={liveScreenshot || screenshotData}
                      alt="Live device view"
                      onError={(e) => {
                        console.warn('⚠️ Live preview image failed to load/decode', {
                          device: device?.device_name,
                          srcPrefix: String(liveScreenshot || screenshotData || '').slice(0, 40)
                        });
                      }}
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  )}
                </div>
                {deviceResolution.width && deviceResolution.height && (
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#888', textAlign: 'center' }}>
                    Device resolution: {deviceResolution.width} x {deviceResolution.height}
                  </div>
                )}

                {/* Floating Keyboard Input Panel */}
                {showKeyboardInput && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: '#fff',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                      zIndex: 1000,
                      minWidth: '320px',
                      maxWidth: '90%',
                      border: '2px solid #4CAF50'
                    }}
                  >
                    <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h4 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
                        ⌨️ Keyboard Input
                      </h4>
                      <button
                        onClick={() => {
                          setShowKeyboardInput(false);
                          setKeyboardText('');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '20px',
                          cursor: 'pointer',
                          color: '#999',
                          padding: '0 5px'
                        }}
                        title="Close (ESC)"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div style={{ marginBottom: '10px' }}>
                      <input
                        ref={keyboardInputRef}
                        type="text"
                        value={keyboardText}
                        onChange={(e) => setKeyboardText(e.target.value)}
                        onKeyDown={handleKeyboardKeyPress}
                        placeholder="Type here and press Enter..."
                        disabled={isLoading}
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '14px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        autoFocus
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={sendKeyboardText}
                        disabled={isLoading || !keyboardText.trim()}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: keyboardText.trim() ? '#4CAF50' : '#e0e0e0',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: keyboardText.trim() ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        📤 Send (Enter)
                      </button>
                      <button
                        onClick={() => setKeyboardText('')}
                        disabled={isLoading || !keyboardText}
                        style={{
                          padding: '10px 15px',
                          background: '#ff9800',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: keyboardText ? 'pointer' : 'not-allowed',
                          fontSize: '14px'
                        }}
                        title="Clear text"
                      >
                        🗑️
                      </button>
                    </div>
                    
                    <div style={{ marginTop: '12px', fontSize: '11px', color: '#666', textAlign: 'center' }}>
                      💡 Tip: Press <kbd style={{padding:'2px 6px',background:'#f5f5f5',border:'1px solid #ccc',borderRadius:'3px'}}>Enter</kbd> to send | <kbd style={{padding:'2px 6px',background:'#f5f5f5',border:'1px solid #ccc',borderRadius:'3px'}}>ESC</kbd> to close
                    </div>
                  </div>
                )}

                {/* === NEW: Volume row (mute / slider / beep) — always fits mobile === */}
                <div className="rc2-vol-row">
                  <button
                    type="button"
                    className="rc2-vol-btn rc2-vol-mute"
                    onClick={() => sendKeyEvent(164, 'Mute')}
                    disabled={isLoading}
                    title="Mute"
                    aria-label="Mute"
                  >{I.speakerX}</button>
                  <div className="rc2-vol-mid">
                    <input
                      type="range" min="0" max="100"
                      value={volume}
                      onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                      disabled={isLoading}
                      className="remote-control-volume-slider rc2-vol-slider"
                      style={{ background: `linear-gradient(to right, #1976D2 0%, #1976D2 ${volume}%, #e0e0e0 ${volume}%, #e0e0e0 100%)` }}
                    />
                    <span className="rc2-vol-val">{volume}%</span>
                  </div>
                  <button
                    type="button"
                    className="rc2-vol-btn rc2-vol-beep"
                    onClick={() => handleDeviceAction('Play Beep')}
                    disabled={isLoading}
                    title="Beep"
                    aria-label="Beep"
                  >{I.bell}</button>
                </div>


                {/* === NEW: 4-column elegant keypad === */}
                <div className="rc2-keypad">
                  {/* Row 1: directional — left / right / up / down */}
                  <Rc2Key icon={I.arrLeft}  label="Left"  color="#607D8B" onClick={() => sendKeyEvent(21, 'Left')}  disabled={isLoading} />
                  <Rc2Key icon={I.arrRight} label="Right" color="#607D8B" onClick={() => sendKeyEvent(22, 'Right')} disabled={isLoading} />
                  <Rc2Key icon={I.arrUp}    label="Up"    color="#607D8B" onClick={() => sendKeyEvent(19, 'Up')}    disabled={isLoading} />
                  <Rc2Key icon={I.arrDown}  label="Down"  color="#607D8B" onClick={() => sendKeyEvent(20, 'Down')}  disabled={isLoading} />

                  {/* Row 2: home / menu / back / OK */}
                  <Rc2Key icon={I.home} label="Home" color="#546E7A" onClick={() => sendKeyEvent(3,  'Home')} disabled={isLoading} />
                  <Rc2Key icon={I.menu} label="Menu" color="#546E7A" onClick={() => sendKeyEvent(82, 'Menu')} disabled={isLoading} />
                  <Rc2Key icon={I.back} label="Back" color="#1976D2" onClick={() => sendKeyEvent(4,  'Back')} disabled={isLoading} />
                  <Rc2Key icon={I.ok}   label="OK"   color="#2E7D32" onClick={() => sendKeyEvent(23, 'OK')}   disabled={isLoading} />

                  {/* Row 3: manage (wide-2) / keyboard / reload */}
                  <Rc2Key icon={I.clipboard} label="Player Manager" color="#388E3C" wide onClick={() => setShowQuickManage(true)} disabled={isLoading || !device.is_online} title="Player Manager" />
                  <Rc2Key icon={I.keyboard}  label={showKeyboardInput ? 'Close' : 'Keyboard'}
                                             color={showKeyboardInput ? '#4CAF50' : '#546E7A'}
                                             onClick={() => setShowKeyboardInput(!showKeyboardInput)} disabled={isLoading} />
                  <Rc2Key icon={I.rotate}    label="Reload"   color="#F57C00" onClick={() => handleDeviceAction('Reload App')}     disabled={isLoading} />

                  {/* Row 4: admin-only — Control (wide-2) / AnyDesk (wide-2) */}
                  {isAdmin && (
                    <>
                      <Rc2Key icon={I.gamepad} label="Control" color="#1976D2" wide
                        onClick={async () => {
                          try { await openBridgeWebUi(); }
                          catch (e) { setTimedStatus(`❌ Control open failed: ${e?.message || String(e)}`, 5000); }
                        }}
                        disabled={isLoading || !device.is_online} />
                      <Rc2Key icon={I.cast} label="AnyDesk"
                        color={device.is_online && device.anydesk_id ? '#EF443B' : '#bdbdbd'}
                        wide
                        onClick={openAnyDeskPopup}
                        disabled={isLoading || !device.is_online || !device.anydesk_id}
                        title={!device.is_online ? 'Device is offline' : !device.anydesk_id ? 'AnyDesk ID not configured' : 'Connect via AnyDesk'} />
                    </>
                  )}
                </div>

              </div>

              {/* === NEW: Display section — duration + flash msg + layout + media === */}
              <div className="remote-control-section rc2-display">
                <div className="rc2-display-head">
                  <h3 className="rc2-h3">Display now</h3>
                  <div className="rc2-duration">
                    <label htmlFor="rc2-duration-in">Duration (s)</label>
                    <input
                      id="rc2-duration-in"
                      type="number" min="1" max="300"
                      value={displayLength}
                      onChange={(e) => setDisplayLength(parseInt(e.target.value) || 1)}
                      disabled={isLoading}
                      className="rc2-num"
                    />
                  </div>
                </div>

                {/* Row: flash message (icon | text input | apply) */}
                <div className="rc2-row">
                  <div className="rc2-row-icon" title="Flash message">{I.msg}</div>
                  <input
                    type="text"
                    value={textMessage}
                    onChange={(e) => setTextMessage(e.target.value)}
                    placeholder="Type a message to flash on screen…"
                    disabled={isLoading}
                    className="rc2-input"
                  />
                  <button
                    type="button"
                    className="rc2-apply"
                    onClick={handleShowMessage}
                    disabled={isLoading || !textMessage.trim()}
                    title="Apply / send"
                    aria-label="Apply"
                  >{I.check}</button>
                  <span className="rc2-spacer" />
                </div>

                {/* Row: Layout (icon | dropdown | apply | back-to-sch) */}
                <div className="rc2-row">
                  <div className="rc2-row-icon" title="Layout">{I.layout}</div>
                  <select
                    className="rc2-select"
                    value={selectedLayout}
                    onChange={(e) => setSelectedLayout(e.target.value)}
                    disabled={isLoading || (layouts || []).length === 0}
                  >
                    <option value="">{(layouts || []).length === 0 ? 'No layouts' : 'Select layout…'}</option>
                    {(layouts || []).map((l) => {
                      const name = typeof l === 'string' ? l : l.name;
                      return <option key={name} value={name}>{name}</option>;
                    })}
                  </select>
                  <button
                    type="button"
                    className="rc2-apply"
                    onClick={handleSetLayout}
                    disabled={isLoading || !selectedLayout}
                    title="Activate selected layout"
                    aria-label="Activate layout"
                  >{I.check}</button>
                  <button
                    type="button"
                    className="rc2-back"
                    onClick={handleClearLayout}
                    disabled={isLoading}
                    title="Back to scheduled layout"
                    aria-label="Back to scheduled"
                  >{I.schedule}</button>
                </div>

                {/* Row: Media — 2026-05-19, custom thumbnail dropdown.
                    Native <select> can't render images; this is a
                    button-triggered panel that lists files with small
                    preview thumbs. Image files render as <img>; videos
                    show 🎬 + filename; everything else gets 📄. */}
                <div className="rc2-row" style={{ position: 'relative' }}>
                  <div className="rc2-row-icon" title="Media file">{I.film}</div>
                  <MediaFileDropdown
                    files={deviceFiles}
                    selected={selectedFile}
                    onSelect={setSelectedFile}
                    disabled={isLoading || operationInProgress || deviceFiles.length === 0}
                  />
                  <button
                    type="button"
                    className="rc2-apply"
                    onClick={handleShowFile}
                    disabled={isLoading || !selectedFile}
                    title="Show this file"
                    aria-label="Show file"
                  >{I.check}</button>
                  <button
                    type="button"
                    className="rc2-back"
                    onClick={handleClearLayout}
                    disabled={isLoading}
                    title="Back to scheduled content"
                    aria-label="Back to scheduled"
                  >{I.schedule}</button>
                </div>

                {/* Media-playback strip — target selector + prev / play-pause / next in ONE row */}
                <div className="rc2-playback-row">
                  <select
                    value={mediaControlTarget}
                    onChange={(e) => setMediaControlTarget(e.target.value)}
                    disabled={isLoading}
                    className="rc2-select rc2-select-target"
                  >
                    <option value="background">Background Music</option>
                    <option value="main">Main zone</option>
                  </select>
                  <button type="button" className="rc2-apply rc2-pb"               onClick={() => handleMediaControl('prev')}       disabled={isLoading} title="Previous"   aria-label="Previous">{I.skipBack}</button>
                  <button type="button" className="rc2-apply rc2-pb rc2-pb-play"  onClick={() => handleMediaControl('play_pause')} disabled={isLoading} title={isPaused ? 'Resume' : 'Pause'} aria-label="Play/Pause">{isPaused ? I.play : I.pause}</button>
                  <button type="button" className="rc2-apply rc2-pb"               onClick={() => handleMediaControl('next')}       disabled={isLoading} title="Next"        aria-label="Next">{I.skipFwd}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {showQuickManage && (
        <DeviceQuickManageModal
          device={device}
          isAdmin={isAdmin}
          onClose={() => setShowQuickManage(false)}
          onDeviceChanged={onDeviceChanged}
        />
      )}
    </div>
  );
};

export default RemoteControlModal;